/**
 * Recorte deliberado del router real: solo lo que el backend de este spike
 * sostiene hoy. Tracking, importacion, auditoria, reporte, admin y MFA se
 * retiran de las rutas -- no se dejan como paginas rotas. AppShell ya oculta
 * esos enlaces de nav cuando el permiso falta, asi que el usuario nunca ve
 * un vinculo muerto.
 *
 * Reconectados en pasadas anteriores (backend ya los sostenia desde los
 * Sprints 5 y 7, solo faltaba el frontend): tablero institucional, tablero
 * de unidad y el ciclo de revision -- este ultimo vive DENTRO de
 * ActivityDetail, no como paginas separadas (ver nota en AppShell.tsx).
 *
 * Reconectado: el asistente de 9 pasos completo (ActivityWizard.tsx), en
 * /actividades/nueva (crear) y /actividades/:id/editar (editar) -- antes
 * ambos eran vinculos muertos desde ActivitiesList.tsx y ActivityDetail.tsx.
 *
 * Reconectado en esta pasada: seguimiento del levantamiento (Tracking.tsx,
 * /seguimiento) -- modulo separado del RAT, backend nuevo en src/tracking.ts.
 *
 * Nuevo (no existia en el sistema original, ver src/catalog-admin.ts):
 * catalogos administrables (CatalogsAdmin.tsx, /catalogos) -- gateado con
 * el mismo permiso 'institutional.view' que el tablero institucional, no
 * uno granular nuevo (mismo colapso documentado en el README del backend).
 *
 * Reconectado en esta pasada (existia como backend puro, sin UI ni rutas
 * HTTP -- ver src/admin.ts): consola de usuarios (UsersAdmin.tsx,
 * /usuarios), mismo gateo que catalogos.
 *
 * Reconectado en esta pasada (existia solo /audit/verify, un veredicto sin
 * forma de ver los eventos -- ver src/audit.ts): Auditoria (Audit.tsx,
 * /bitacora), mismo gateo. El alcance de que se audita esta documentado
 * en la cabecera de src/audit.ts. Ruta deliberadamente NO "/auditoria":
 * choca por prefijo con el proxy de Vite para la API "/audit" (Vite hace
 * match de proxy por prefijo del pathname, no por ruta exacta), lo que
 * reenvia la navegacion de pagina completa al Worker en vez de servir la
 * SPA -- encontrado probando esta misma pantalla en local.
 *
 * Reconstruido para igualar el alcance real del proyecto original (rama
 * `main`, commit 5979987 -- ver src/import.ts): importacion CON STAGING Y
 * CURACION (Import.tsx, /actividades/importar) -- lee cualquier .xlsx/.csv
 * (SheetJS, todas las hojas), mapeo de columnas editable, resolucion de
 * unidad por nombre editable, vista previa incluir/excluir. Dos tipos:
 * "Actividades (a borrador)" y "Contactos de seguimiento". Gateada con
 * 'activity.create' a nivel de PAGINA (capa 1, UX -- mismo permiso que
 * "+ Nueva actividad"); el backend revalida por fila y por tipo --
 * "Contactos" en particular exige institucional (mismo colapso que el
 * resto de /seguimiento, ver tracking.ts), asi que un colaborador puede
 * ENTRAR a la pagina pero sus filas de tipo contacto salen 'forbidden'
 * al importar, igual que pasaria si el propio backend rechaza cualquier
 * otra escritura fuera de su unidad.
 *
 * Reconectado en esta pasada (backend completo desde el principio,
 * src/mfa.ts -- solo faltaba el frontend): MFA (MfaEnroll.tsx en
 * /mfa/inscribir, MfaChallenge.tsx en /mfa/verificar). Rutas HERMANAS de
 * /login, FUERA de RequireSession -- durante el segundo factor no hay
 * sesion real todavia, solo un "pending token" de 5 minutos (ver
 * AuthProvider.tsx). guards.tsx ya redirigia aqui desde antes (mfa ===
 * 'not_enrolled'/'needs_challenge'); solo faltaba que estas dos rutas
 * existieran. Apagado por defecto (`auth.require_mfa` en app_settings),
 * asi que hoy nunca se activa solo con este cambio.
 *
 * Nuevo (no existia en el sistema original): reportes de cumplimiento
 * exportables (Report.tsx, /reportes institucional y
 * /reportes/unidad/:unitId por unidad). Sin backend nuevo -- reusa
 * fetchInstitutionalDashboard/fetchUnitDashboard/fetchActivities y agrega
 * una vista pensada para `window.print()` (el CSS `@media print` ya vivia
 * en styles.css sin que ninguna pantalla lo usara). Autorizacion resuelta
 * DENTRO del componente, igual que institucional/unidad -- no via
 * RequirePermission (que solo acepta un permiso, no un OR de dos).
 */
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { can, type MyAuthz } from '@rat/shared';
import { AppShell } from './components/AppShell';
import { RequirePermission, RequireSession } from './auth/guards';
import { useAuth } from './auth/AuthProvider';
import { Login } from './pages/Login';
import { MfaEnroll } from './pages/MfaEnroll';
import { MfaChallenge } from './pages/MfaChallenge';
import { ActivitiesList } from './pages/ActivitiesList';
import { ActivityDetail } from './pages/ActivityDetail';
import { ActivityWizard } from './pages/ActivityWizard';
import { Import } from './pages/Import';
import { InstitutionalDashboard } from './pages/InstitutionalDashboard';
import { UnitDashboard } from './pages/UnitDashboard';
import { Report } from './pages/Report';
import { Tracking } from './pages/Tracking';
import { CatalogsAdmin } from './pages/CatalogsAdmin';
import { UsersAdmin } from './pages/UsersAdmin';
import { Audit } from './pages/Audit';
import { NotFound, NoUnit, Unauthorized } from './pages/Misc';

/**
 * "Inicio" por rol -- a pedido explicito del DPD: quien administra (o
 * audita) el sitio quiere caer en un tablero, no en la lista plana de
 * actividades. NO reusa `landingRoute()` de @rat/shared tal cual (esa
 * funcion es del proyecto original, donde 'institutional.view' ya
 * distinguia administrar de solo-leer): en este backend 'institutional.view'
 * tambien gatea Catalogos/Usuarios/Auditoria (colapso deliberado, ver
 * AppShell.tsx), asi que un auditor necesita el chequeo aparte por
 * 'unit.read.all' (ver AuthProvider.tsx:toMyAuthz) para llegar al mismo
 * tablero sin heredar permisos de administracion que no tiene.
 */
function landingRouteFor(authz: MyAuthz): string {
  if (can(authz, 'institutional.view') || can(authz, 'unit.read.all')) return '/institucional';
  const first = authz.units[0];
  return first ? `/unidad/${first.unit_id}` : '/sin-unidad';
}

function Landing() {
  const { authz } = useAuth();
  return <Navigate to={landingRouteFor(authz)} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/mfa/inscribir', element: <MfaEnroll /> },
  { path: '/mfa/verificar', element: <MfaChallenge /> },
  {
    path: '/',
    element: (
      <RequireSession>
        <AppShell />
      </RequireSession>
    ),
    children: [
      { index: true, element: <Landing /> },
      { path: 'sin-unidad', element: <NoUnit /> },
      { path: 'actividades', element: <ActivitiesList /> },
      { path: 'actividades/nueva', element: <ActivityWizard /> },
      {
        path: 'actividades/importar',
        element: (
          <RequirePermission perm="activity.create">
            <Import />
          </RequirePermission>
        ),
      },
      { path: 'actividades/:id', element: <ActivityDetail /> },
      { path: 'actividades/:id/editar', element: <ActivityWizard /> },
      { path: 'institucional', element: <InstitutionalDashboard /> },
      { path: 'unidad/:unitId', element: <UnitDashboard /> },
      { path: 'reportes', element: <Report /> },
      { path: 'reportes/unidad/:unitId', element: <Report /> },
      { path: 'seguimiento', element: <Tracking /> },
      {
        path: 'catalogos',
        element: (
          <RequirePermission perm="institutional.view">
            <CatalogsAdmin />
          </RequirePermission>
        ),
      },
      {
        path: 'usuarios',
        element: (
          <RequirePermission perm="institutional.view">
            <UsersAdmin />
          </RequirePermission>
        ),
      },
      {
        path: 'bitacora',
        element: (
          <RequirePermission perm="institutional.view">
            <Audit />
          </RequirePermission>
        ),
      },
      { path: 'no-autorizado', element: <Unauthorized /> },
      { path: 'no-encontrado', element: <NotFound /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
